"use client";
import React, { useState } from "react";
import { Caveat } from "next/font/google";
import Image from "next/image";
import { motion } from "framer-motion";
import { StartForm } from "../../start/StartClient";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });


/* ─────────────────────────────────────────────────────────────
   1. HOTEL WEBSITE READY FOR BOOKINGS SECTION
   Exact reproduction of Odoo's Hotel Website Booking interface
   ───────────────────────────────────────────────────────────── */
export function HotelWebsiteBookingSection({ section }) {
  const title = section?.title || "Website ready for bookings";
  const leftText = section?.leftText || "Create a website using Odoo's AI-based builder, 3+ million free photos, drag-and-drop elements, and SEO optimization tools. No coding skills required.";
  const rightText = section?.rightText || "List your availabilities, details of each room, and allow clients to make reservations online.";

  // State for mobile card interactivity
  const [selectedBed, setSelectedBed] = useState("double");
  const [selectedBreakfast, setSelectedBreakfast] = useState("included");
  const [guestCount, setGuestCount] = useState(1);
  const [nightCount, setNightCount] = useState(1);

  return (
    <section className="py-12 sm:py-20 lg:py-24 px-3 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">

        {/* SECTION HEADER WITH HANDWRITTEN HIGHLIGHT */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-8 sm:mb-10 text-left"
        >
          <h2 className={`${caveat.className} text-3xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-white leading-tight flex flex-wrap items-center gap-2 sm:gap-3`}>
            <span>Website ready for</span>
            <span className="relative inline-block px-3 sm:px-4 py-0.5 sm:py-1 bg-[#F1BF38] text-slate-900 rounded-xl sm:rounded-2xl font-bold font-sans text-2xl sm:text-4xl lg:text-5xl shadow-md transform -rotate-1">
              bookings
            </span>
          </h2>
        </motion.div>

        {/* 2-COLUMN COPYWRITING SUBTEXT */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid md:grid-cols-2 gap-6 sm:gap-8 lg:gap-16 mb-8 sm:mb-12 text-slate-300 text-sm sm:text-base lg:text-lg leading-relaxed max-w-5xl"
        >
          <div>
            <p className="text-xl text-gray-300 leading-relaxed">
              <strong className="text-white font-bold">Create a website</strong> using Odoo's AI-based builder, 3+ million free photos, drag-and-drop elements, and SEO optimization tools. No coding skills required.
            </p>
          </div>
          <div>
            <p className="text-xl text-gray-300 leading-relaxed">
              <strong className="text-white font-bold">List your availabilities</strong>, details of each room, and allow clients to make reservations online.
            </p>
          </div>
        </motion.div>

        {/* MOCKUP DISPLAY CONTAINER */}
        <div className="relative grid lg:grid-cols-12 gap-8 items-start">

          {/* Subtle curved background decoration behind mobile card on desktop */}
          <div className="absolute right-0 top-0 bottom-0 w-1/3 bg-slate-100/10 rounded-l-[60px] pointer-events-none -z-10 hidden lg:block" />

          {/* LEFT MOCKUP: DESKTOP HOTEL WEBSITE ROOMS CATALOG (8 Cols) */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-8 bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 w-full"
          >
            {/* Desktop Header / Navigation Bar */}
            <div className="bg-white border-b border-slate-100 px-3 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3 text-xs sm:text-sm">
              <div className="flex items-center gap-4 sm:gap-6">
                {/* Hotel Logo */}
                <div className="flex items-center gap-2 font-semibold text-slate-900 tracking-wider">
                  <div className="w-7 h-7 rounded-full border border-slate-700 flex items-center justify-center text-slate-800 font-bold text-xs">
                    ❖
                  </div>
                  <div className="flex flex-col leading-none">
                    <span className="text-[11px] tracking-widest font-bold">NOVA</span>
                    <span className="text-[8px] text-slate-500 tracking-widest">HOTEL</span>
                  </div>
                </div>

                {/* Nav items */}
                <nav className="flex items-center gap-3 sm:gap-4 text-slate-600 font-medium text-xs">
                  <span className="cursor-pointer hover:text-slate-900">Home</span>
                  <span className="cursor-pointer text-slate-900 font-bold border-b-2 border-slate-800 pb-0.5">Rooms</span>
                </nav>
              </div>

              {/* Header Right Actions */}
              <div className="flex items-center gap-2 sm:gap-4 text-slate-600 text-xs">
                <button className="p-1 hover:bg-slate-100 rounded">🛒</button>
                <button className="p-1 hover:bg-slate-100 rounded">🔍</button>
                <span className="hidden md:inline-block text-slate-500 font-mono text-[11px]">📞 +1 555-555-5556</span>
                <span className="hidden sm:inline-block cursor-pointer hover:text-slate-900 text-slate-600 font-medium">Sign in</span>
                <button className="bg-[#7BA694] hover:bg-[#689482] text-white px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-[11px] sm:text-xs font-medium transition-colors shadow-sm">
                  Contact Us
                </button>
              </div>
            </div>

            {/* Desktop Layout Body: Sidebar + Room Catalog Grid */}
            <div className="p-3 sm:p-6 bg-slate-50/50 flex flex-col md:flex-row gap-4 sm:gap-6">

              {/* Left Sidebar Filter */}
              <div className="w-full md:w-48 flex-shrink-0 space-y-3 sm:space-y-4 text-xs">
                {/* Rental Period Filter */}
                <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm space-y-2">
                  <div className="flex justify-between items-center font-bold text-slate-800 cursor-pointer">
                    <span>Rental Period</span>
                    <span className="text-slate-400 text-[10px]">▲</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-1 gap-2 sm:gap-1.5 pt-1">
                    <div className="relative">
                      <input
                        type="text"
                        readOnly
                        value="Start date"
                        className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded text-slate-400 text-[11px] focus:outline-none"
                      />
                      <span className="absolute right-2 top-2 text-[10px]">📅</span>
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        readOnly
                        value="End date"
                        className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded text-slate-400 text-[11px] focus:outline-none"
                      />
                      <span className="absolute right-2 top-2 text-[10px]">📅</span>
                    </div>
                  </div>
                </div>

                {/* Guests Filter */}
                <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm space-y-2">
                  <div className="flex justify-between items-center font-bold text-slate-800 cursor-pointer">
                    <span>Guests</span>
                    <span className="text-slate-400 text-[10px]">▲</span>
                  </div>
                  <select className="w-full px-2 py-1.5 bg-slate-50 border border-slate-200 rounded text-slate-700 text-[11px] focus:outline-none cursor-pointer">
                    <option>All Guests</option>
                    <option>1 Guest</option>
                    <option>2 Guests</option>
                    <option>3+ Guests</option>
                  </select>
                </div>
              </div>

              {/* Main Room Cards Grid */}
              <div className="flex-1 space-y-4">

                {/* Top Controls: Title, Search, Sort */}
                <div className="flex flex-wrap items-center justify-between gap-2.5 bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                  <h3 className="font-bold text-slate-900 text-sm">All rooms</h3>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search.."
                        className="px-2.5 py-1 pr-6 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-600 focus:outline-none w-24 sm:w-36"
                      />
                      <span className="absolute right-2 top-1.5 text-[10px] text-slate-400">🔍</span>
                    </div>
                    <div className="text-[11px] text-slate-500 flex items-center gap-1">
                      <span>Sort By:</span>
                      <select className="bg-transparent font-semibold text-slate-800 cursor-pointer focus:outline-none">
                        <option>Featured</option>
                        <option>Price: Low to High</option>
                        <option>Price: High to Low</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Room Cards Grid (Responsive: 1 col on XS, 2 on SM, 3 on MD+) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  {/* Card 1: Deluxe Suite */}
                  <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden group hover:shadow-md transition-all">
                    <div className="h-32 relative overflow-hidden bg-slate-100">
                      <img
                        src="https://images.unsplash.com/photo-1590490360182-c33d57733427?auto=format&fit=crop&w=500&q=80"
                        alt="Deluxe Suite"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-bold text-slate-900 text-xs">Deluxe Suite</h4>
                      <p className="text-[11px] text-slate-600 mt-0.5">
                        from <strong className="text-slate-900 font-bold">195.00 €</strong> per Night
                      </p>
                    </div>
                  </div>

                  {/* Card 2: Deluxe Room */}
                  <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden group hover:shadow-md transition-all">
                    <div className="h-32 relative overflow-hidden bg-slate-100">
                      <img
                        src="https://images.unsplash.com/photo-1618773928121-c32242e63f39?auto=format&fit=crop&w=500&q=80"
                        alt="Deluxe Room"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-bold text-slate-900 text-xs">Deluxe Room</h4>
                      <p className="text-[11px] text-slate-600 mt-0.5">
                        from <strong className="text-slate-900 font-bold">125.00 €</strong> per Night
                      </p>
                    </div>
                  </div>

                  {/* Card 3: Standard Room */}
                  <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden group hover:shadow-md transition-all">
                    <div className="h-32 relative overflow-hidden bg-slate-100">
                      <img
                        src="https://images.unsplash.com/photo-1566665797739-1674de7a421a?auto=format&fit=crop&w=500&q=80"
                        alt="Standard Room"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-3">
                      <h4 className="font-bold text-slate-900 text-xs">Standard Room</h4>
                      <p className="text-[11px] text-slate-600 mt-0.5">
                        from <strong className="text-slate-900 font-bold">95.00 €</strong> per Night
                      </p>
                    </div>
                  </div>

                  {/* Row 2 Room Cards */}
                  <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden group hover:shadow-md transition-all">
                    <div className="h-28 relative overflow-hidden bg-slate-100">
                      <img
                        src="https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=500&q=80"
                        alt="Executive Suite"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-2.5">
                      <h4 className="font-bold text-slate-900 text-xs">Executive Suite</h4>
                      <p className="text-[10px] text-slate-600 mt-0.5">
                        from <strong className="text-slate-900 font-bold">220.00 €</strong> per Night
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden group hover:shadow-md transition-all">
                    <div className="h-28 relative overflow-hidden bg-slate-100">
                      <img
                        src="https://images.unsplash.com/photo-1591088398332-8a7791972843?auto=format&fit=crop&w=500&q=80"
                        alt="Penthouse"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-2.5">
                      <h4 className="font-bold text-slate-900 text-xs">Penthouse Suite</h4>
                      <p className="text-[10px] text-slate-600 mt-0.5">
                        from <strong className="text-slate-900 font-bold">310.00 €</strong> per Night
                      </p>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden group hover:shadow-md transition-all">
                    <div className="h-28 relative overflow-hidden bg-slate-100">
                      <img
                        src="https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&w=500&q=80"
                        alt="Family Suite"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-2.5">
                      <h4 className="font-bold text-slate-900 text-xs">Family Suite</h4>
                      <p className="text-[10px] text-slate-600 mt-0.5">
                        from <strong className="text-slate-900 font-bold">180.00 €</strong> per Night
                      </p>
                    </div>
                  </div>

                </div>

              </div>

            </div>
          </motion.div>

          {/* RIGHT MOCKUP: MOBILE ROOM RESERVATION PANEL (4 Cols) */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-4 bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden text-slate-800 p-4 sm:p-6 space-y-3.5 sm:space-y-4 max-w-sm sm:max-w-md mx-auto w-full lg:max-w-none"
          >
            {/* Mobile Header Bar */}
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full border border-slate-700 flex items-center justify-center text-[10px] font-bold">
                  ❖
                </div>
                <div className="flex flex-col leading-none">
                  <span className="text-[10px] font-bold tracking-widest text-slate-900">NOVA</span>
                  <span className="text-[7px] text-slate-500 tracking-widest">HOTEL</span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-600 text-xs">
                <button className="hover:text-slate-900">🛒</button>
                <button className="hover:text-slate-900 font-bold">≡</button>
              </div>
            </div>

            {/* Room Title & Rating */}
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight">Deluxe Room</h3>
              <div className="flex items-center gap-1.5 text-slate-400 text-xs mt-1">
                <span>☆☆☆☆☆</span>
                <span className="text-[11px] text-slate-500">(0 review)</span>
              </div>
            </div>

            {/* Room Pricing */}
            <div className="text-slate-800">
              <span className="text-xl sm:text-2xl font-bold text-slate-900">145.00 €</span>
              <span className="text-slate-500 text-xs"> / 1 Night</span>
            </div>

            <hr className="border-slate-200 my-2" />

            {/* Beds Options */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 block">Beds</label>
              <div className="flex flex-wrap gap-1.5 sm:gap-2 text-xs">
                <button
                  onClick={() => setSelectedBed("double")}
                  className={`px-3 py-1.5 rounded-full border text-[11px] sm:text-xs transition-colors ${selectedBed === "double"
                    ? "bg-[#E6F3EC] border-[#7BA694] text-[#2D5A43] font-semibold"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                >
                  Large double bed
                </button>
                <button
                  onClick={() => setSelectedBed("single")}
                  className={`px-3 py-1.5 rounded-full border text-[11px] sm:text-xs transition-colors ${selectedBed === "single"
                    ? "bg-[#E6F3EC] border-[#7BA694] text-[#2D5A43] font-semibold"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                >
                  Single beds
                </button>
              </div>
            </div>

            {/* Breakfast Options */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 block">Breakfast</label>
              <div className="flex flex-wrap gap-1.5 sm:gap-2 text-xs">
                <button
                  onClick={() => setSelectedBreakfast("included")}
                  className={`px-3 py-1.5 rounded-full border text-[11px] sm:text-xs transition-colors ${selectedBreakfast === "included"
                    ? "bg-[#E6F3EC] border-[#7BA694] text-[#2D5A43] font-semibold"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                >
                  Breakfast Included
                </button>
                <button
                  onClick={() => setSelectedBreakfast("excluded")}
                  className={`px-3 py-1.5 rounded-full border text-[11px] sm:text-xs transition-colors ${selectedBreakfast === "excluded"
                    ? "bg-[#E6F3EC] border-[#7BA694] text-[#2D5A43] font-semibold"
                    : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                >
                  Breakfast Not Included
                </button>
              </div>
            </div>

            {/* Guests Select */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 block">Guests</label>
              <select
                value={guestCount}
                onChange={(e) => setGuestCount(Number(e.target.value))}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none cursor-pointer"
              >
                <option value={1}>1 pers.</option>
                <option value={2}>2 pers.</option>
                <option value={3}>3 pers.</option>
                <option value={4}>4 pers.</option>
              </select>
            </div>

            {/* Rental Period */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 block">Rental Period</label>
              <div className="space-y-2">
                <div className="relative">
                  <input
                    type="text"
                    readOnly
                    value="09/10/2025"
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none"
                  />
                  <span className="absolute right-3 top-2.5 text-xs">📅</span>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    readOnly
                    value="09/11/2025"
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none"
                  />
                  <span className="absolute right-3 top-2.5 text-xs">📅</span>
                </div>
              </div>
            </div>

            {/* Action Bar: Quantity & Book Button */}
            <div className="pt-2 flex items-center justify-between gap-3">
              <div className="flex items-center border border-slate-200 rounded-full px-3 py-1.5 text-xs font-semibold bg-slate-50 gap-3">
                <button
                  onClick={() => setNightCount(Math.max(1, nightCount - 1))}
                  className="text-slate-400 hover:text-slate-800 text-sm font-bold"
                >
                  -
                </button>
                <span>{nightCount}</span>
                <button
                  onClick={() => setNightCount(nightCount + 1)}
                  className="text-slate-400 hover:text-slate-800 text-sm font-bold"
                >
                  +
                </button>
              </div>

              <button className="flex-1 bg-[#7BA694] hover:bg-[#689482] text-white px-4 sm:px-5 py-2.5 rounded-full flex items-center justify-center gap-2 text-xs font-semibold shadow-md transition-all transform active:scale-95">
                <span>🛒</span>
                <span>Book</span>
              </button>
            </div>

          </motion.div>

        </div>

        {/* CALLOUT BADGE MOBILE RESPONSIVE */}
        <div className="w-full flex justify-center mt-12 sm:mt-20">
          <div className="flex items-center justify-center gap-2 sm:gap-3 max-w-full px-2">
            <Image
              width={54}
              height={54}
              className="rounded-full border border-white/20 shadow-lg flex-shrink-0 w-11 h-11 sm:w-14 sm:h-14"
              alt="Odoo Specialist"
              src="/odoo/snippets/s_wd_persona/bubu.webp"
            />
            <div className="bg-white/10 border border-white/20 rounded-full shadow-2xl p-2.5 sm:p-3 px-4 sm:px-6 backdrop-blur-md text-center">
              <i className="text-white text-xs sm:text-base font-semibold block leading-snug">
                Your domain is free for one year with Odoo!
              </i>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   2. SAVE TIME, MAXIMIZE BOOKINGS SECTION
   Exact reproduction of Odoo's Hotel Schedule & Planning UI
   ───────────────────────────────────────────────────────────── */
export function HotelSaveTimeMaximizeBookingsSection({ section }) {
  return (
    <section className="py-12 sm:py-20 lg:py-24 px-3 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">

          {/* LEFT COLUMN: GANTT SCHEDULE MOCKUP + DOODLE CALENDAR ICON */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative w-full overflow-hidden"
          >
            {/* Background Decorative Curve */}
            <div className="absolute -left-12 -top-12 -bottom-12 w-4/5 bg-slate-100/10 rounded-[60px] pointer-events-none -z-10 hidden lg:block" />

            {/* Main Odoo Schedule Table Mockup Card */}
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-xs w-full">

              {/* Odoo App Top Bar */}
              <div className="bg-slate-100 border-b border-slate-200 px-3 sm:px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-[11px]">
                <div className="flex items-center gap-3 sm:gap-4 text-slate-700 font-medium">
                  <span className="flex items-center gap-1 font-bold text-slate-900">
                    <span className="text-amber-500">🏨</span> Hotel
                  </span>
                  <span className="hover:text-slate-900 cursor-pointer font-bold border-b border-slate-600">Schedule</span>
                  <span className="hover:text-slate-900 cursor-pointer hidden sm:inline-block">Orders</span>
                  <span className="hover:text-slate-900 cursor-pointer hidden sm:inline-block">Rooms</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 text-slate-500">
                  <span className="font-semibold text-slate-700 text-[10px] sm:text-xs">NOVA Hotel</span>
                  <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-slate-300 inline-block"></span>
                </div>
              </div>

              {/* Toolbar */}
              <div className="p-2.5 sm:p-3 bg-white border-b border-slate-100 flex flex-wrap items-center justify-between gap-2 text-[11px]">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <button className="bg-purple-700 hover:bg-purple-800 text-white px-2 sm:px-2.5 py-1 rounded font-semibold text-[10px]">
                    New
                  </button>
                  <button className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 sm:px-2.5 py-1 rounded font-semibold text-[10px]">
                    Publish
                  </button>
                  <span className="text-slate-600 cursor-pointer px-0.5 sm:px-1 text-[10px] sm:text-[11px]">Actions ▾</span>
                  <span className="text-slate-600 cursor-pointer px-0.5 sm:px-1 text-[10px] sm:text-[11px]">Schedule ℹ</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="bg-slate-100 px-2 py-0.5 rounded border border-slate-200 text-slate-600 text-[10px]">
                    Resource ✕ Search...
                  </span>
                  <div className="flex border border-slate-200 rounded overflow-hidden text-[10px]">
                    <span className="px-1.5 py-0.5 bg-slate-200 font-bold">≡</span>
                    <span className="px-1.5 py-0.5 bg-white">📅</span>
                    <span className="px-1.5 py-0.5 bg-white">📊</span>
                  </div>
                </div>
              </div>

              {/* Date Controls Bar */}
              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-700 font-medium">
                <div className="flex items-center gap-1.5">
                  <button className="px-1.5 py-0.5 border border-slate-300 rounded bg-white text-[10px]">←</button>
                  <button className="px-1.5 py-0.5 border border-slate-300 rounded bg-white text-[10px]">→</button>
                  <span className="font-bold border border-slate-300 px-1.5 py-0.5 rounded bg-white text-[10px]">Week ▾</span>
                  <button className="text-slate-500 hover:text-slate-800 text-[10px]">Today</button>
                </div>
                <span className="font-bold text-slate-800 text-[10px] sm:text-[11px]">
                  Week 37, Sep 7 - Sep 13
                </span>
              </div>

              {/* Gantt Grid Table (Scrollable horizontally on mobile phones) */}
              <div className="overflow-x-auto max-w-full touch-pan-x">
                <table className="w-full text-left border-collapse text-[10px]">
                  <thead>
                    <tr className="bg-slate-100/80 border-b border-slate-200 text-slate-600 font-semibold">
                      <th className="p-2 border-r border-slate-200 min-w-[120px]">Schedule</th>
                      <th className="p-2 border-r border-slate-200 text-center min-w-[75px]">Sunday 7</th>
                      <th className="p-2 border-r border-slate-200 text-center min-w-[90px]">Monday 8</th>
                      <th className="p-2 border-r border-slate-200 text-center min-w-[90px] bg-amber-50/60 text-slate-900 font-bold">Tuesday 9</th>
                      <th className="p-2 border-r border-slate-200 text-center min-w-[90px]">Wednesday 10</th>
                      <th className="p-2 text-center min-w-[75px]">Thursday 11</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200/60 font-mono text-[9.5px]">

                    {/* Row 101 Standard Room */}
                    <tr className="hover:bg-slate-50">
                      <td className="p-1.5 border-r border-slate-200 font-sans font-semibold text-slate-800">
                        📌 101 (Standard Room)
                        <div className="text-[8px] text-emerald-600 font-mono">10h / 168h (5.95%)</div>
                      </td>
                      <td colSpan={4} className="p-1 border-r border-slate-200">
                        <div className="bg-sky-500/90 text-white p-1 rounded font-sans text-[9px] truncate shadow-sm">
                          Mike Brown - Standard Room - S00010 (Breakfast Included, 1 pers.)
                        </div>
                      </td>
                      <td></td>
                    </tr>

                    {/* Row 102 Standard Room */}
                    <tr className="hover:bg-slate-50">
                      <td className="p-1.5 border-r border-slate-200 font-sans font-semibold text-slate-800">
                        📌 102 (Standard Room)
                        <div className="text-[8px] text-emerald-600 font-mono">57h / 168h (33.93%)</div>
                      </td>
                      <td></td>
                      <td colSpan={3} className="p-1 border-r border-slate-200">
                        <div className="bg-sky-500/90 text-white p-1 rounded font-sans text-[9px] truncate shadow-sm">
                          Sophie Ramirez - Standard Room - S00011 (Breakfast Included, 1 pers.)
                        </div>
                      </td>
                      <td></td>
                    </tr>

                    {/* Row 103 Standard Room */}
                    <tr className="hover:bg-slate-50">
                      <td className="p-1.5 border-r border-slate-200 font-sans font-semibold text-slate-800">
                        📌 103 (Standard Room)
                        <div className="text-[8px] text-emerald-600 font-mono">45h / 168h (26.79%)</div>
                      </td>
                      <td colSpan={2} className="p-1 border-r border-slate-200">
                        <div className="bg-purple-600/90 text-white p-1 rounded font-sans text-[9px] truncate shadow-sm">
                          Linn Yan - Standard Room - S00010 (Breakfast Not Incl...)
                        </div>
                      </td>
                      <td></td>
                      <td></td>
                      <td></td>
                    </tr>

                    {/* Row 104 Standard Room */}
                    <tr className="hover:bg-slate-50">
                      <td className="p-1.5 border-r border-slate-200 font-sans font-semibold text-slate-800">
                        📌 104 (Standard Room)
                        <div className="text-[8px] text-emerald-600 font-mono">57h / 168h (33.93%)</div>
                      </td>
                      <td></td>
                      <td colSpan={3} className="p-1 border-r border-slate-200">
                        <div className="bg-sky-500/90 text-white p-1 rounded font-sans text-[9px] truncate shadow-sm">
                          Ethan McAllister - Standard Room - S00012 (Breakfast Included, 2 pers.)
                        </div>
                      </td>
                      <td></td>
                    </tr>

                    {/* Row 301 Deluxe Room */}
                    <tr className="hover:bg-slate-50">
                      <td className="p-1.5 border-r border-slate-200 font-sans font-semibold text-slate-800">
                        📌 301 (Deluxe Room)
                        <div className="text-[8px] text-emerald-600 font-mono">57h / 168h (33.93%)</div>
                      </td>
                      <td></td>
                      <td colSpan={2} className="p-1 border-r border-slate-200">
                        <div className="bg-purple-600/90 text-white p-1 rounded font-sans text-[9px] truncate shadow-sm">
                          Amanda White - Deluxe Room - S00007 (Breakfast Included, 1 pers.)
                        </div>
                      </td>
                      <td></td>
                      <td></td>
                    </tr>

                    {/* Row 302 Deluxe Room */}
                    <tr className="hover:bg-slate-50">
                      <td className="p-1.5 border-r border-slate-200 font-sans font-semibold text-slate-800">
                        📌 302 (Deluxe Room)
                        <div className="text-[8px] text-emerald-600 font-mono">90h / 168h (53.57%)</div>
                      </td>
                      <td colSpan={2} className="p-1 border-r border-slate-200">
                        <div className="bg-sky-500/90 text-white p-1 rounded font-sans text-[9px] truncate shadow-sm">
                          Lana Kim - Deluxe Room - S00013 [Breakfast Included, 2 pers.]
                        </div>
                      </td>
                      <td colSpan={2} className="p-1 border-r border-slate-200">
                        <div className="bg-sky-500/90 text-white p-1 rounded font-sans text-[9px] truncate shadow-sm">
                          Marcus Patel - Deluxe Room - S00014
                        </div>
                      </td>
                      <td></td>
                    </tr>

                    {/* Row 303 Deluxe Room */}
                    <tr className="hover:bg-slate-50">
                      <td className="p-1.5 border-r border-slate-200 font-sans font-semibold text-slate-800">
                        📌 303 (Deluxe Room)
                        <div className="text-[8px] text-emerald-600 font-mono">57h / 168h (33.93%)</div>
                      </td>
                      <td></td>
                      <td colSpan={3} className="p-1 border-r border-slate-200">
                        <div className="bg-purple-600/90 text-white p-1 rounded font-sans text-[9px] truncate shadow-sm">
                          Silverwood Dynamics - Deluxe Room - S00016 (Breakfast Included, 1 pers.)
                        </div>
                      </td>
                      <td></td>
                    </tr>

                  </tbody>
                </table>
              </div>

            </div>

            {/* FLOATING TEAL DOODLE CALENDAR ICON BELOW MOCKUP */}
            <div className="mt-4 sm:mt-6 flex justify-center lg:justify-start lg:ml-20">
              <div className="relative w-16 h-16 sm:w-24 sm:h-24 select-none transform -rotate-12 hover:rotate-0 transition-transform duration-300">
                <svg viewBox="0 0 100 100" fill="none" className="w-full h-full drop-shadow-xl">
                  <rect x="18" y="24" width="68" height="58" rx="14" fill="#0077C5" />
                  <rect x="14" y="20" width="68" height="58" rx="14" fill="#00A09D" stroke="#0077C5" strokeWidth="4" />
                  <rect x="30" y="10" width="10" height="18" rx="5" fill="#00A09D" stroke="#0077C5" strokeWidth="4" />
                  <rect x="60" y="10" width="10" height="18" rx="5" fill="#00A09D" stroke="#0077C5" strokeWidth="4" />
                  <circle cx="32" cy="42" r="4" fill="#FFFFFF" />
                  <circle cx="48" cy="42" r="4" fill="#FFFFFF" />
                  <circle cx="64" cy="42" r="4" fill="#FFFFFF" />
                  <circle cx="32" cy="58" r="4" fill="#FFFFFF" />
                  <circle cx="48" cy="58" r="4" fill="#FFFFFF" />
                  <circle cx="64" cy="58" r="4" fill="#FFFFFF" />
                </svg>
              </div>
            </div>

          </motion.div>

          {/* RIGHT COLUMN: HEADING WITH TEAL BRUSH UNDERLINE & COPY */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-5 space-y-4 sm:space-y-6 text-left"
          >
            {/* Doodle Sunburst Rays above Title */}
            <div className="text-[#00A09D]">
              <svg className="w-10 h-10 sm:w-12 sm:h-12 stroke-current overflow-visible" viewBox="0 0 48 48" fill="none">
                <path d="M 8 36 L 2 24 M 24 30 L 24 6 M 40 36 L 46 24" strokeWidth="4.5" strokeLinecap="round" />
              </svg>
            </div>

            {/* Handwritten Heading with Teal Highlight */}
            <h2 className={`${caveat.className} text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]`}>
              <div>Save time,</div>
              <div>
                <span className="relative inline-block">
                  maximize
                  <svg
                    className="absolute -bottom-1 left-0 w-full h-3.5 sm:h-4 text-[#00A09D] overflow-visible"
                    viewBox="0 0 120 14"
                    fill="none"
                  >
                    <path
                      d="M 2 8 Q 60 2, 118 8 M 8 11 Q 60 5, 110 11"
                      stroke="#00A09D"
                      strokeWidth="5"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </div>
              <div>bookings</div>
            </h2>

            {/* Subtext Copy */}
           <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Manage your planning easily.</strong> Bookings, modifications, room upgrades, and additional options. All this is directly linked to your invoicing for a great follow-up.
              </p>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   3. GUEST-CENTRIC OPERATIONS SECTION
   Exact reproduction of Odoo's Guest Check-In & Rental Orders UI
   ───────────────────────────────────────────────────────────── */
export function HotelGuestCentricOperationsSection({ section }) {
  return (
    <section className="py-12 sm:py-20 lg:py-24 px-3 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">

          {/* LEFT COLUMN: HANDWRITTEN TITLE & COPY */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 space-y-6 sm:space-y-8 text-left"
          >
            {/* Title with Blue Wavy Underline */}
            <h2 className={`${caveat.className} text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]`}>
              <div>
                Guest-
                <span className="relative inline-block px-1">
                  centric
                  <svg
                    className="absolute -bottom-2 left-0 w-full h-4 text-[#00A09D] overflow-visible"
                    viewBox="0 0 120 16"
                    fill="none"
                  >
                    <path
                      d="M 2 8 C 30 2, 60 14, 90 4 C 105 0, 115 10, 118 8"
                      stroke="#00A09D"
                      strokeWidth="5"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
              </div>
              <div className="mt-1">operations</div>
            </h2>

            {/* Paragraph 1 & 2 */}
            <div>
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">An effortless check-in and check-out system</strong> ensures you can focus on making your guests feel truly welcome.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                <strong className="text-white font-bold">Include additional services</strong> such as breakfast or activities, and collect tourist tax. You can also track guests&apos; ID information if your country requires it.
              </p>
            </div>
          </motion.div>

          {/* RIGHT COLUMN: OVERLAPPING RENTAL ORDER & GUEST STATUS UI MOCKUPS */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 relative w-full"
          >


            <div className="relative space-y-4">

              {/* BACK MOCKUP: RENTAL ORDER FORM CARD (Odoo S00004) */}
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-xs w-full transform md:-rotate-0 transition-transform duration-300">

                {/* Navbar */}
                <div className="bg-slate-100 border-b border-slate-200 px-3 sm:px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-[11px]">
                  <div className="flex items-center gap-3 text-slate-700 font-medium">
                    <span className="font-bold text-slate-900 flex items-center gap-1">
                      <span className="text-purple-600">🔑</span> Rental
                    </span>
                    <span className="cursor-pointer hover:text-slate-900">Orders</span>
                    <span className="cursor-pointer hover:text-slate-900 hidden sm:inline-block">Schedule</span>
                    <span className="cursor-pointer hover:text-slate-900 hidden sm:inline-block">Products</span>
                    <span className="cursor-pointer hover:text-slate-900 hidden md:inline-block">Reporting</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-500 text-[10px]">
                    <span className="font-semibold text-slate-700">NOVA Hotel</span>
                    <span className="w-4 h-4 rounded-full bg-slate-300"></span>
                  </div>
                </div>

                {/* Actions Bar */}
                <div className="p-2.5 sm:p-3 bg-white border-b border-slate-100 flex flex-wrap items-center justify-between gap-2 text-[10px]">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button className="bg-purple-700 hover:bg-purple-800 text-white px-2.5 py-1 rounded font-semibold">
                      New
                    </button>
                    <span className="font-bold text-slate-800 px-1">Rental Orders / S00004</span>
                    <button className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 rounded font-bold shadow-sm">
                      Check In
                    </button>
                    <button className="border border-slate-300 px-2 py-1 rounded text-slate-700 hover:bg-slate-50">
                      Send
                    </button>
                    <button className="border border-slate-300 px-2 py-1 rounded text-slate-700 hover:bg-slate-50 hidden sm:inline-block">
                      Preview
                    </button>
                    <button className="border border-slate-300 px-2 py-1 rounded text-slate-700 hover:bg-slate-50 hidden sm:inline-block">
                      Cancel
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-semibold text-[10px]">
                      Booked
                    </span>
                    <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[10px]">
                      Sales Order
                    </span>
                  </div>
                </div>

                {/* Form Details */}
                <div className="p-3 sm:p-5 bg-slate-50/40 space-y-3 text-[11px]">
                  <div className="flex justify-between items-start">
                    <h4 className="text-xl font-bold text-slate-900 font-mono">S00004</h4>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-slate-700">
                    <div className="space-y-1">
                      <div className="flex gap-2">
                        <span className="w-24 text-slate-500 font-medium">Customer:</span>
                        <div>
                          <strong className="text-sky-600 cursor-pointer block">Mike Brown</strong>
                          <span className="text-[10px] text-slate-500 block">Rue Brederode 18, 1000 Bruxelles, Belgium</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <span className="w-24 text-slate-500 font-medium">Invoice Address:</span>
                        <span className="text-slate-800">Mike Brown</span>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <div className="flex gap-2">
                        <span className="w-24 text-slate-500 font-medium">Order Date:</span>
                        <span className="text-slate-800 font-mono">Sep 9, 8:40 AM</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="w-24 text-slate-500 font-medium">Pricelist:</span>
                        <span className="text-slate-800">Default (EUR)</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="w-24 text-slate-500 font-medium">Payment Terms:</span>
                        <span className="text-slate-800 font-semibold">Immediate Payment</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="w-24 text-slate-500 font-medium">Rental period:</span>
                        <span className="text-slate-800 font-mono text-[10px]">Sep 16, 6:00 PM → Sep 19, 9:00 AM</span>
                      </div>
                    </div>
                  </div>
                </div>

              </div>

              {/* FOREGROUND OVERLAPPING GUEST STATUS CARDS GRID */}
              <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 p-3 sm:p-4 text-slate-800 space-y-2.5 sm:-mt-6 relative z-10 w-full md:w-[95%] mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs">

                  {/* Guest Tile 1 */}
                  <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl flex justify-between items-center hover:bg-slate-100/80 transition-colors">
                    <div>
                      <h5 className="font-bold text-slate-900 text-xs">Ethan McAllister</h5>
                      <span className="text-[10px] text-slate-500 font-mono block">S00006</span>
                      <span className="text-[10px] text-slate-600 block">Pickup: Sep 10, 6:00 PM</span>
                    </div>
                    <div className="text-right space-y-1">
                      <span className="font-bold text-slate-900 text-xs block">195.00 €</span>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-semibold text-[9px] inline-block">
                        Reserved
                      </span>
                    </div>
                  </div>

                  {/* Guest Tile 2 */}
                  <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl flex justify-between items-center hover:bg-slate-100/80 transition-colors">
                    <div>
                      <h5 className="font-bold text-slate-900 text-xs">Lisa Anderson</h5>
                      <span className="text-[10px] text-slate-500 font-mono block">S00005</span>
                      <span className="text-[10px] text-slate-600 block">Pickup: Sep 14, 6:00 PM</span>
                    </div>
                    <div className="text-right space-y-1">
                      <span className="font-bold text-slate-900 text-xs block">950.00 €</span>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-semibold text-[9px] inline-block">
                        Reserved
                      </span>
                    </div>
                  </div>

                  {/* Guest Tile 3 */}
                  <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl flex justify-between items-center hover:bg-slate-100/80 transition-colors">
                    <div>
                      <h5 className="font-bold text-slate-900 text-xs">Mike Brown</h5>
                      <span className="text-[10px] text-slate-500 font-mono block">S00004</span>
                      <span className="text-[10px] text-slate-600 block">Pickup: Sep 16, 6:00 PM</span>
                    </div>
                    <div className="text-right space-y-1">
                      <span className="font-bold text-slate-900 text-xs block">600.00 €</span>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 rounded font-semibold text-[9px] inline-block">
                        Reserved
                      </span>
                    </div>
                  </div>

                  {/* Guest Tile 4 */}
                  <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl flex justify-between items-center hover:bg-slate-100/80 transition-colors">
                    <div>
                      <h5 className="font-bold text-slate-900 text-xs">Sarah Davis</h5>
                      <span className="text-[10px] text-slate-500 font-mono block">S00003</span>
                      <span className="text-[10px] text-amber-700 font-medium block">⚠️ Late Return: Sep 9, 9:00 AM</span>
                    </div>
                    <div className="text-right space-y-1">
                      <span className="font-bold text-slate-900 text-xs block">1,024.70 €</span>
                      <span className="px-2 py-0.5 bg-amber-500 text-white rounded font-semibold text-[9px] inline-block">
                        Pickedup
                      </span>
                    </div>
                  </div>

                  {/* Guest Tile 5 */}
                  <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl flex justify-between items-center hover:bg-slate-100/80 transition-colors">
                    <div>
                      <h5 className="font-bold text-slate-900 text-xs">Amanda White</h5>
                      <span className="text-[10px] text-slate-500 font-mono block">S00002</span>
                      <span className="text-[10px] text-slate-600 block">Return: Sep 19, 9:00 AM</span>
                    </div>
                    <div className="text-right space-y-1">
                      <span className="font-bold text-slate-900 text-xs block">2,759.40 €</span>
                      <span className="px-2 py-0.5 bg-amber-500 text-white rounded font-semibold text-[9px] inline-block">
                        Pickedup
                      </span>
                    </div>
                  </div>

                  {/* Guest Tile 6 */}
                  <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-xl flex justify-between items-center hover:bg-slate-100/80 transition-colors">
                    <div>
                      <h5 className="font-bold text-slate-900 text-xs">Lee Portal</h5>
                      <span className="text-[10px] text-slate-500 font-mono block">S00001</span>
                    </div>
                    <div className="text-right space-y-1">
                      <span className="font-bold text-slate-900 text-xs block">729.70 €</span>
                      <span className="px-2 py-0.5 bg-slate-200 text-slate-700 rounded font-semibold text-[9px] inline-block">
                        Returned
                      </span>
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </motion.div>

        </div>

        {/* BOTTOM PERSONA CALLOUT SPEECH BUBBLE */}
        <div className="w-full flex justify-center mt-12 sm:mt-20">
          <div className="flex items-center justify-center gap-2 sm:gap-3 max-w-full px-2">
            <Image
              width={54}
              height={54}
              className="rounded-full border border-white/20 shadow-lg flex-shrink-0 w-11 h-11 sm:w-14 sm:h-14 object-cover"
              alt="Odoo Specialist"
              src="/odoo/snippets/s_wd_persona/bee.webp"
            />
            <div className="bg-white/10 text-slate-800 rounded-full shadow-2xl p-2.5 sm:p-3 px-5 sm:px-8 border border-slate-100 text-center">
              <i className="text-white text-xs sm:text-base font-semibold block leading-snug">
                Tracking finances has never been this easy!
              </i>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   4. FLEXIBLE INVOICING SECTION
   Exact reproduction of Odoo's Hotel Invoice & Quotation #S00004
   ───────────────────────────────────────────────────────────── */
export function HotelFlexibleInvoicingSection({ section }) {
  return (
    <section className="py-12 sm:py-20 lg:py-24 px-3 sm:px-8 lg:px-16 relative overflow-hidden bg-transparent text-white font-sans w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-center">

          {/* LEFT COLUMN: TITLE WITH PINK DOTTED HIGHLIGHT & COPY */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 space-y-6 sm:space-y-8 text-left relative"
          >
            {/* Pink Sparkles & Arrow Doodle Graphic
            <div className="hidden sm:block absolute -top-8 right-8 text-rose-400 select-none">
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none" className="overflow-visible">
                <path d="M 36 12 L 48 24 L 38 26 L 46 36 L 40 40 L 32 30 L 28 38 Z" fill="#FF6B6B" stroke="#000000" strokeWidth="2.5" />
                <line x1="16" y1="20" x2="8" y2="16" stroke="#FF6B6B" strokeWidth="3" strokeLinecap="round" />
                <line x1="22" y1="34" x2="14" y2="40" stroke="#FF6B6B" strokeWidth="3" strokeLinecap="round" />
                <line x1="28" y1="12" x2="26" y2="4" stroke="#FF6B6B" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </div> */}

            {/* Handwritten Heading with Pink Dotted Line Under "Flexible" */}
            <h2 className={`${caveat.className} text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]`}>
              <div>
                <span className="relative inline-block px-1">
                  Flexible
                  {/* Pink Dotted Underline */}
                  <svg
                    className="absolute -bottom-2 left-0 w-full h-3 text-rose-400 overflow-visible"
                    viewBox="0 0 140 12"
                    fill="none"
                  >
                    <path
                      d="M 4 6 Q 70 2, 136 6"
                      stroke="#FF6B6B"
                      strokeWidth="6"
                      strokeDasharray="2 10"
                      strokeLinecap="round"
                    />
                  </svg>
                </span>
                <span className="ml-3">invoicing</span>
              </div>
            </h2>

            {/* Subtext Paragraph */}
            <p className="text-xl text-gray-300 leading-relaxed">
              <strong className="text-white font-bold">Generate professional invoices</strong> in one click, and accept various payment methods.
            </p>
          </motion.div>

          {/* RIGHT COLUMN: PROFESSIONAL QUOTATION #S00004 PDF DOCUMENT MOCKUP */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="lg:col-span-7 relative w-full flex justify-center"
          >

            {/* Document Card Container */}
            <div className="bg-white rounded-xl shadow-2xl overflow-hidden border border-slate-200 text-slate-800 text-xs w-full max-w-lg p-2 sm:p-3 space-y-2 font-sans">

              {/* Document Header: Logo & Address */}
              <div className="flex justify-between items-start border-b border-slate-100 pb-4">
                <div className="flex items-center gap-2 font-semibold text-slate-900 tracking-wider">
                  <div className="w-8 h-8 rounded-full border border-slate-700 flex items-center justify-center text-slate-800 font-bold text-xs">
                    ❖
                  </div>
                  <div className="flex flex-col leading-none">
                    <span className="text-xs tracking-widest font-bold">NOVA</span>
                    <span className="text-[9px] text-slate-500 tracking-widest">HOTEL</span>
                  </div>
                </div>

                <div className="text-right text-[10px] text-slate-600 leading-tight">
                  <strong className="block text-slate-800 font-bold">NOVA Hotel</strong>
                  <span>Avenue Louise 237</span><br />
                  <span>1050 Ixelles</span><br />
                  <span>Belgium</span>
                </div>
              </div>

              {/* Customer Billing Info */}
              <div className="text-[11px] text-slate-700 space-y-0.5 max-w-xs ml-auto text-right sm:text-left sm:ml-0">
                <strong className="block text-slate-900 font-bold">Mike Brown</strong>
                <span>Rue Brederode 18</span><br />
                <span>1000 Bruxelles</span><br />
                <span>Belgium</span>
              </div>

              {/* Quotation Title & Meta Info */}
              <div className="space-y-3 pt-2">
                <h3 className="text-lg sm:text-xl font-bold text-slate-900 font-sans">
                  Quotation # S00004
                </h3>

                <div className="grid grid-cols-3 gap-2 text-[10px] text-slate-600 border-t border-b border-slate-100 py-2">
                  <div>
                    <span className="block text-slate-400 font-medium">Quotation Date:</span>
                    <strong className="text-slate-800">09/09/2025</strong>
                  </div>
                  <div>
                    <span className="block text-slate-400 font-medium">Expiration:</span>
                    <strong className="text-slate-800">10/09/2025</strong>
                  </div>
                  <div>
                    <span className="block text-slate-400 font-medium">Salesperson:</span>
                    <strong className="text-slate-800">Administrator</strong>
                  </div>
                </div>
              </div>

              {/* Invoice Item Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-[10px]">
                  <thead>
                    <tr className="border-b-2 border-slate-800 text-slate-900 font-bold uppercase tracking-wider text-[9px]">
                      <th className="py-2 pr-2">DESCRIPTION</th>
                      <th className="py-2 px-2 text-right">QUANTITY</th>
                      <th className="py-2 px-2 text-right">UNIT PRICE</th>
                      <th className="py-2 px-2 text-right">TAXES</th>
                      <th className="py-2 pl-2 text-right">AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-[10px]">
                    <tr>
                      <td className="py-3 pr-2 font-medium text-slate-800">
                        Standard Room (Breakfast Not Included, 2 pers. + 1 child)
                        <span className="block text-[9px] text-slate-500 font-mono mt-0.5">
                          9/16/25, 6:00 PM to 9/20/25, 6:00 PM
                        </span>
                      </td>
                      <td className="py-3 px-2 text-right text-slate-700">1.00 Units</td>
                      <td className="py-3 px-2 text-right text-slate-700">600.00 €</td>
                      <td className="py-3 px-2 text-right text-slate-700">21%</td>
                      <td className="py-3 pl-2 text-right font-bold text-slate-900">495.87 €</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Subtotal & Total Calculation */}
              <div className="w-full sm:w-64 ml-auto space-y-1.5 text-[11px] pt-2">
                <div className="flex justify-between text-slate-600 border-b border-slate-100 pb-1">
                  <span>Untaxed Amount</span>
                  <span className="font-semibold text-slate-800">495.87 €</span>
                </div>
                <div className="flex justify-between text-slate-600 border-b border-slate-100 pb-1">
                  <span>VAT 21%</span>
                  <span className="font-semibold text-slate-800">104.13 €</span>
                </div>
                <div className="flex justify-between bg-slate-800 text-white font-bold p-2 rounded">
                  <span>Total</span>
                  <span>600.00 €</span>
                </div>
              </div>

              {/* Document Terms Footer */}
              <div className=" border-t border-slate-100 text-[9px] text-slate-500 space-y-0">
                <p>Terms &amp; Conditions: <span className="text-sky-600 font-mono">https://www.novahotel.be/terms</span></p>
                <p>Payment terms: <span className="text-slate-700 font-medium">Immediate Payment</span></p>
              </div>

              {/* PDF Footer Bar */}
              <div className=" border-t border-slate-200 flex justify-between items-center text-[8px] text-slate-400 font-mono">
                <div>+32 2 691 3277 | https://www.novahotel.be | booking@novahotel.be</div>
                <div>Page 1/1</div>
              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
   5. HOLIDAY HOUSE GET STARTED SECTION
   Reuses the exact StartForm from StartClient.jsx configured for Holiday House
   ───────────────────────────────────────────────────────────── */
export function HolidayHouseGetStartedSection({ section }) {
  const industryName = section?.industryName || "Holiday House";
  return (
    <section className="py-8 sm:py-16 px-3 sm:px-8 relative overflow-hidden bg-[#070709] text-white font-sans w-full z-10">
      <StartForm defaultIndustry={industryName} />
    </section>
  );
}

export function IndustryGetStartedSection({ section, industryName: defaultName }) {
  const name = section?.industryName || defaultName || "Guided Tours";
  return (
    <section className="py-8 sm:py-16 px-3 sm:px-8 relative overflow-hidden bg-[#070709] text-white font-sans w-full z-10">
      <StartForm defaultIndustry={name} />
    </section>
  );
}




